import React, { useState } from 'react';
import {
Box,
Container,
Typography,
Card,
CardContent,
Grid,
Avatar,
Rating,
Chip,
Divider,
Paper,
useTheme,
useMediaQuery,
IconButton,
Button,
Stack,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
Slide
} from '@mui/material';
import {
FormatQuote as QuoteIcon,
School as SchoolIcon,
BusinessCenter as BusinessIcon,
Science as ScienceIcon,
Engineering as EngineeringIcon,
Share as ShareIcon,
ThumbUp as ThumbUpIcon,
Facebook,
Twitter,
LinkedIn,
ChevronLeft,
ChevronRight,
PlayArrow,
Pause
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import NavigationMenu from '../navigation/NavigationMenu';

const Testimonials = () => {
const theme = useTheme();
const isMobile = useMediaQuery(theme.breakpoints.down('md'));
const [currentIndex, setCurrentIndex] = useState(0);
const [autoPlay, setAutoPlay] = useState(true);
const [selectedTestimonial, setSelectedTestimonial] = useState(null);
const [openDialog, setOpenDialog] = useState(false);

const testimonials = [
    {
    id: 1,
    name: 'Dr. Sarah Chen',
    role: 'Senior Research Scientist',
    company: 'MIT Research Lab',
    avatar: '/static/images/avatar/1.jpg',
    content: 'The Research and Innovations Academy has revolutionized how our team collaborates. The platform\'s intuitive interface and comprehensive tools have accelerated our research projects by 40%. The learning resources are exceptional.',
    rating: 5,
    category: 'academia',
    date: '2 weeks ago',
    featured: true
    },
    {
    id: 2,
    name: 'Michael Rodriguez',
    role: 'PhD Candidate',
    company: 'Stanford University',
    avatar: '/static/images/avatar/2.jpg',
    content: 'As a graduate student, the resources and community here have been invaluable. The mentorship programs and research tools helped me publish my first paper. The platform truly understands academic needs.',
    rating: 5,
    category: 'student',
    date: '1 month ago',
    featured: true
    },
    {
    id: 3,
    name: 'Dr. Emily Watson',
    role: 'Professor & Department Head',
    company: 'Harvard University',
    avatar: '/static/images/avatar/3.jpg',
    content: 'Outstanding platform for academic collaboration. The innovative features and responsive support team make it an essential tool for modern research. Our department has seen a significant improvement in research output.',
    rating: 4,
    category: 'academia',
    date: '3 weeks ago',
    featured: false
    },
    {
    id: 4,
    name: 'Alex Thompson',
    role: 'Innovation Lead',
    company: 'Google Research',
    avatar: '/static/images/avatar/4.jpg',
    content: 'The platform bridges the gap between academia and industry perfectly. We\'ve collaborated with multiple universities through this platform, leading to groundbreaking innovations. The rating system ensures continuous improvement.',
    rating: 5,
    category: 'industry',
    date: '2 months ago',
    featured: true
    },
    {
    id: 5,
    name: 'Dr. James Wilson',
    role: 'Research Director',
    company: 'NASA Jet Propulsion Lab',
    avatar: '/static/images/avatar/5.jpg',
    content: 'The data analysis tools and collaboration features have transformed our research workflow. We can now collaborate seamlessly with international partners. The platform\'s security and reliability are exceptional.',
    rating: 5,
    category: 'research',
    date: '1 week ago',
    featured: false
    },
    {
    id: 6,
    name: 'Lisa Zhang',
    role: 'AI Research Engineer',
    company: 'OpenAI',
    avatar: '/static/images/avatar/6.jpg',
    content: 'The machine learning resources and community discussions have been incredibly helpful. I\'ve learned advanced techniques that directly applied to my work. The platform fosters genuine innovation.',
    rating: 4,
    category: 'industry',
    date: '3 months ago',
    featured: false
    }
];

const categories = [
    { key: 'all', label: 'All Testimonials', icon: <QuoteIcon />, count: testimonials.length },
    { key: 'academia', label: 'Academia', icon: <SchoolIcon />, count: testimonials.filter(t => t.category === 'academia').length },
    { key: 'industry', label: 'Industry', icon: <BusinessIcon />, count: testimonials.filter(t => t.category === 'industry').length },
    { key: 'student', label: 'Students', icon: <SchoolIcon />, count: testimonials.filter(t => t.category === 'student').length },
    { key: 'research', label: 'Research', icon: <ScienceIcon />, count: testimonials.filter(t => t.category === 'research').length }
];

const [selectedCategory, setSelectedCategory] = useState('all');

const filteredTestimonials = selectedCategory === 'all' 
    ? testimonials 
    : testimonials.filter(testimonial => testimonial.category === selectedCategory);

const featuredTestimonials = testimonials.filter(t => t.featured);

// Auto-play carousel
React.useEffect(() => {
    if (!autoPlay) return;

    const interval = setInterval(() => {
    setCurrentIndex((prev) => 
        prev === featuredTestimonials.length - 1 ? 0 : prev + 1
    );
    }, 5000);

    return () => clearInterval(interval);
}, [autoPlay, featuredTestimonials.length]);

const nextTestimonial = () => {
    setCurrentIndex((prev) => 
    prev === featuredTestimonials.length - 1 ? 0 : prev + 1
    );
};

const prevTestimonial = () => {
    setCurrentIndex((prev) => 
    prev === 0 ? featuredTestimonials.length - 1 : prev - 1
    );
};

const handleTestimonialClick = (testimonial) => {
    setSelectedTestimonial(testimonial);
    setOpenDialog(true);
};

const shareTestimonial = (platform) => {
    const text = `"${selectedTestimonial.content}" - ${selectedTestimonial.name}`;
    const url = window.location.href;
    
    const shareUrls = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
    };

    window.open(shareUrls[platform], '_blank', 'width=600,height=400');
};

const getCategoryIcon = (category) => {
    switch (category) {
    case 'academia': return <SchoolIcon />;
    case 'industry': return <BusinessIcon />;
    case 'student': return <SchoolIcon />;
    case 'research': return <ScienceIcon />;
    default: return <EngineeringIcon />;
    }
};

const getCategoryColor = (category) => {
    switch (category) {
    case 'academia': return 'primary';
    case 'industry': return 'secondary';
    case 'student': return 'success';
    case 'research': return 'error';
    default: return 'default';
    }
};

return (
    <Box  sx={{ display: 'flex', minHeight: '100vh' }}>
    <NavigationMenu />
    <Container maxWidth="lg" sx={{ marginTop: 8, py: 4, ml: isMobile ? 0 : '240px' }}>
        {/* Header Section */}
        {/* Header Section */}
        <Slide direction="down" in={true} mountOnEnter unmountOnExit>
            <Box sx={{
                mb: 3,
                p: 3,
                borderRadius: 3,
                background: theme.palette.success.main,
                color: 'white',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2
            }}>
                <Box>
                    <Typography variant="h5" component="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                        What Our Community Says
                    </Typography>
                    <Typography sx={{ opacity: 0.9 }}>
                        Discover how researchers, academics, and industry professionals are transforming their work with our platform
                    </Typography>
                </Box>
            </Box>
        </Slide>

        {/* Featured Testimonials Carousel */}
        <Box mb={8}>
        <Typography variant="h6" component="h6" gutterBottom textAlign="center" sx={{ mb: 4 }}>
            Featured Stories
        </Typography>
        
        <Box sx={{ position: 'relative', maxWidth: '800px', mx: 'auto' }}>
            <Card sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: 6 }}>
            <AnimatePresence mode="wait">
                <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.5 }}
                >
                <CardContent sx={{ p: 6, textAlign: 'center' }}>
                    <QuoteIcon 
                    sx={{ 
                        fontSize: 48, 
                        color: 'primary.light', 
                        mb: 2,
                        opacity: 0.3 
                    }} 
                    />
                    
                    <Typography 
                    variant="h5" 
                    sx={{ 
                        fontStyle: 'italic', 
                        mb: 4, 
                        lineHeight: 1.6,
                        color: 'text.primary'
                    }}
                    >
                    "{featuredTestimonials[currentIndex]?.content}"
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                    <Avatar 
                        src={featuredTestimonials[currentIndex]?.avatar} 
                        sx={{ 
                        width: 80, 
                        height: 80, 
                        mr: 3,
                        border: `3px solid ${theme.palette.primary.main}`
                        }}
                    />
                    <Box textAlign="left">
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {featuredTestimonials[currentIndex]?.name}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                        {featuredTestimonials[currentIndex]?.role}
                        </Typography>
                        <Typography variant="body2" color="primary.main">
                        {featuredTestimonials[currentIndex]?.company}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <Rating 
                            value={featuredTestimonials[currentIndex]?.rating} 
                            readOnly 
                            size="small" 
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            {featuredTestimonials[currentIndex]?.date}
                        </Typography>
                        </Box>
                    </Box>
                    </Box>
                </CardContent>
                </motion.div>
            </AnimatePresence>

            {/* Navigation Controls */}
            <Box sx={{ 
                position: 'absolute', 
                top: '50%', 
                left: 0, 
                right: 0, 
                display: 'flex', 
                justifyContent: 'space-between',
                px: 2,
                transform: 'translateY(-50%)'
            }}>
                <IconButton 
                onClick={prevTestimonial}
                sx={{ 
                    bgcolor: 'background.paper',
                    boxShadow: 2,
                    '&:hover': { bgcolor: 'primary.main', color: 'white' }
                }}
                >
                <ChevronLeft />
                </IconButton>
                <IconButton 
                onClick={nextTestimonial}
                sx={{ 
                    bgcolor: 'background.paper',
                    boxShadow: 2,
                    '&:hover': { bgcolor: 'primary.main', color: 'white' }
                }}
                >
                <ChevronRight />
                </IconButton>
            </Box>

            {/* Auto-play Control */}
            <Box sx={{ position: 'absolute', bottom: 16, right: 16 }}>
                <IconButton 
                onClick={() => setAutoPlay(!autoPlay)}
                color={autoPlay ? 'primary' : 'default'}
                >
                {autoPlay ? <Pause /> : <PlayArrow />}
                </IconButton>
            </Box>

            {/* Indicators */}
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                pb: 2,
                gap: 1 
            }}>
                {featuredTestimonials.map((_, index) => (
                <Box
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: currentIndex === index ? 'primary.main' : 'grey.300',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        bgcolor: 'primary.main',
                        transform: 'scale(1.2)'
                    }
                    }}
                />
                ))}
            </Box>
            </Card>
        </Box>
        </Box>

        {/* Category Filter */}
        <Box mb={6}>
        <Typography variant="h5" component="h3" gutterBottom textAlign="center">
            Browse by Category
        </Typography>
        <Stack 
            direction={isMobile ? "column" : "row"} 
            spacing={2} 
            justifyContent="center"
            alignItems="center"
            sx={{ mb: 4 }}
        >
            {categories.map((category) => (
            <Chip
                key={category.key}
                icon={category.icon}
                label={`${category.label} (${category.count})`}
                onClick={() => setSelectedCategory(category.key)}
                color={selectedCategory === category.key ? 'primary' : 'default'}
                variant={selectedCategory === category.key ? 'filled' : 'outlined'}
                sx={{ 
                py: 2,
                '& .MuiChip-icon': { color: 'inherit' }
                }}
            />
            ))}
        </Stack>
        </Box>

        {/* Testimonials Grid */}
        <Grid container spacing={3}>
        {filteredTestimonials.map((testimonial, index) => (
            <Grid item xs={12} md={6} lg={4} key={testimonial.id}>
            <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
            >
                <Card 
                sx={{ 
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 6
                    }
                }}
                onClick={() => handleTestimonialClick(testimonial)}
                >
                <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                    <Avatar 
                        src={testimonial.avatar} 
                        sx={{ 
                        width: 60, 
                        height: 60, 
                        mr: 2,
                        border: `2px solid ${theme.palette.primary.main}`
                        }}
                    />
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {testimonial.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                        {testimonial.role}
                        </Typography>
                        <Chip
                        icon={getCategoryIcon(testimonial.category)}
                        label={testimonial.category}
                        size="small"
                        color={getCategoryColor(testimonial.category)}
                        variant="outlined"
                        sx={{ mt: 0.5 }}
                        />
                    </Box>
                    </Box>

                    <Rating value={testimonial.rating} readOnly size="small" sx={{ mb: 2 }} />

                    <Typography 
                    variant="body2" 
                    sx={{ 
                        fontStyle: 'italic',
                        flex: 1,
                        display: '-webkit-box',
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                    }}
                    >
                    "{testimonial.content}"
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                        {testimonial.date}
                    </Typography>
                    {testimonial.featured && (
                        <Chip 
                        icon={<ThumbUpIcon />} 
                        label="Featured" 
                        size="small" 
                        color="primary" 
                        variant="filled"
                        />
                    )}
                    </Box>
                </CardContent>
                </Card>
            </motion.div>
            </Grid>
        ))}
        </Grid>

        {/* Statistics Section */}
        <Box 
        component={Paper} 
        elevation={3} 
        sx={{ 
            mt: 8, 
            p: 6, 
            borderRadius: 4,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            textAlign: 'center'
        }}
        >
        <Grid container spacing={4}>
            <Grid item xs={6} md={3}>
            <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
                500+
            </Typography>
            <Typography variant="h6">
                Happy Researchers
            </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
            <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
                4.8/5
            </Typography>
            <Typography variant="h6">
                Average Rating
            </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
            <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
                95%
            </Typography>
            <Typography variant="h6">
                Would Recommend
            </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
            <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
                50+
            </Typography>
            <Typography variant="h6">
                Institutions
            </Typography>
            </Grid>
        </Grid>
        </Box>

        {/* Testimonial Detail Dialog */}
        <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        >
        {selectedTestimonial && (
            <>
            <DialogTitle sx={{ 
                background: 'linear-gradient(45deg, #1976d2, #00bcd4)',
                color: 'white'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <QuoteIcon sx={{ mr: 2 }} />
                Testimonial Details
                </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 4 }}>
                <Avatar 
                    src={selectedTestimonial.avatar} 
                    sx={{ 
                    width: 80, 
                    height: 80, 
                    mr: 3,
                    border: `3px solid ${theme.palette.primary.main}`
                    }}
                />
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {selectedTestimonial.name}
                    </Typography>
                    <Typography variant="h6" color="primary.main">
                    {selectedTestimonial.role}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                    {selectedTestimonial.company}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Rating value={selectedTestimonial.rating} readOnly />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        {selectedTestimonial.date}
                    </Typography>
                    </Box>
                </Box>
                </Box>

                <Paper sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
                <QuoteIcon sx={{ fontSize: 32, color: 'primary.light', mb: 2, opacity: 0.5 }} />
                <Typography variant="body1" sx={{ fontStyle: 'italic', lineHeight: 1.8 }}>
                    "{selectedTestimonial.content}"
                </Typography>
                </Paper>
            </DialogContent>
            <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
                <Box>
                <Typography variant="body2" color="text.secondary">
                    Share this testimonial:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <IconButton onClick={() => shareTestimonial('twitter')} color="primary">
                    <Twitter />
                    </IconButton>
                    <IconButton onClick={() => shareTestimonial('facebook')} color="primary">
                    <Facebook />
                    </IconButton>
                    <IconButton onClick={() => shareTestimonial('linkedin')} color="primary">
                    <LinkedIn />
                    </IconButton>
                </Box>
                </Box>
                <Button onClick={() => setOpenDialog(false)} variant="contained">
                Close
                </Button>
            </DialogActions>
            </>
        )}
        </Dialog>
    </Container>
    </Box>
);
};

export default Testimonials;